import { Injectable } from "@angular/core";
import { GridDataResult } from "@progress/kendo-angular-grid";

import { BehaviorSubject, Observable } from "rxjs";
import { finalize, map } from "rxjs/operators";
import { BaseHttpReqRespService, NotificationPopupService } from "../../../modules/fwcore/services";
import { AdminService } from "../../admin/services/admin.service";
import { ModelUpdateAudit } from "../models/model-update-audit";
import { RequestModelUpdateAudit, RequestModelUpdateAuditSet, ResponseModelUpdateAuditSet } from "../models/messages/model-update-audit-messages";

@Injectable({
  providedIn: 'root'
})
export class ModelUpdateAuditService extends BehaviorSubject<GridDataResult> {

  public loading: boolean = false;

  protected getGridUrl: string = `Models/model-update-audit`;

  constructor(
    protected adminService: AdminService,
    protected httpTokenService: BaseHttpReqRespService,
    protected notification: NotificationPopupService
  ) {
    super({ data: [], total: 0 });
  }

  // This method is called by the grid component to load data based on the current state (paging, sorting, filtering).
  query(req: RequestModelUpdateAudit): void {
    this.fetch(req).subscribe((x) => super.next(x));
  }

  // Resets the grid to an empty result set, e.g. when no filter criteria is selected.
  clear(): void {
    super.next({ data: [], total: 0 });
  }

  // This method performs the actual data fetching from the server. It sets the loading state to true while the request is in progress and resets it once the request completes.
  fetch(req: RequestModelUpdateAudit): Observable<GridDataResult> {
    this.loading = true;
    return this.getGridData(req).pipe(
      finalize(() => this.loading = false)
    );
  }

  // This method makes an HTTP request to the server to retrieve the grid data based on the provided request parameters. It maps the response to the format expected by the grid component.
  getGridData(req: RequestModelUpdateAudit): Observable<GridDataResult> {
    return this.adminService.getData$<RequestModelUpdateAudit, GridDataResult>(req, this.getGridUrl)
      .pipe(
        map(response => ({
          data: response.data,
          total: response.total
        }))
      );
  }

  // This method is responsible for saving a ModelUpdateAudit item to the server. It constructs a request object, sends it to the server using the admin service, and returns an observable of the response.
  save(item: ModelUpdateAudit): Observable<ResponseModelUpdateAuditSet> {
    const req = new RequestModelUpdateAuditSet();
    req.data = [item];
    return this.adminService.setData$<RequestModelUpdateAuditSet, ResponseModelUpdateAuditSet>(req, this.getGridUrl);
  }
}
